import { ReactNode, useState } from 'react';
import { Button, DropdownItem, IconName, SplitButton } from '@optimus-web/core';
import { DataTestId } from '@mews-commander/enums';
import { lt } from '@mews/framework';
import { ProcessPaymentType } from './ProcessPaymentType';
import { DEFAULT_PROCESS_PAYMENT_TYPES } from './const';
import { getFilteredProcessPaymentDialogTypes } from './utils';
import { useProcessPaymentDialog, UseProcessPaymentDialogProps } from './useProcessPaymentDialog';

export interface ProcessPaymentDialogButtonProps extends Omit<UseProcessPaymentDialogProps, 'onClose' | 'active'> {
    onFormSuccess?: () => void;
    onFormClose?: () => void;
    buttonIcon?: boolean;
    isLoading?: boolean;
    onClick?: () => Promise<void>;
    additionalDropdownOptions?: ReactNode;
    splitButtonTextKeys?: {
        mainButton: Mews.TextKey;
        sideButton: Mews.TextKey;
    };
}

interface ProcessPaymentDialogSplitButton extends Omit<ProcessPaymentDialogButtonProps, 'allowedPaymentTypes'> {
    chargeGuestPaymentTypes: ProcessPaymentType[];
    externalPaymentTypes: ProcessPaymentType[];
    additionalDropdownOptions?: ReactNode;
}

const ProcessPaymentDialogSimpleButton = ({
    onFormClose,
    buttonIcon,
    isLoading,
    onClick,
    splitButtonTextKeys,
    ...props
}: ProcessPaymentDialogButtonProps) => {
    const [renderProcessPaymentDialog, toggleProcessPaymentDialog] = useProcessPaymentDialog({
        ...props,
        title: lt(splitButtonTextKeys?.mainButton ?? 'ProcessPaymentPanelTitleForOptions'),
        onClose: onFormClose,
    });

    const handleClick = async () => {
        await onClick?.();
        toggleProcessPaymentDialog();
    };

    return (
        <>
            <Button
                onClick={handleClick}
                icon={buttonIcon ? IconName.Payments : undefined}
                data-test-id={DataTestId.ProcessPaymentButton}
                loading={isLoading}
                type="button"
            >
                {lt(splitButtonTextKeys?.mainButton ?? 'ProcessPaymentPanelTitleForOptions')}
            </Button>
            {renderProcessPaymentDialog()}
        </>
    );
};

const ProcessPaymentDialogSplitButton = ({
    onFormClose,
    buttonIcon,
    chargeGuestPaymentTypes,
    externalPaymentTypes,
    isLoading,
    onClick,
    splitButtonTextKeys,
    additionalDropdownOptions,
    ...props
}: ProcessPaymentDialogSplitButton) => {
    const [hasChargeGuestPaymentTypes, setHasChargeGuestPaymentTypes] = useState(true);
    const allowedPaymentTypes = hasChargeGuestPaymentTypes ? chargeGuestPaymentTypes : externalPaymentTypes;
    const dialogTitle = hasChargeGuestPaymentTypes
        ? lt(splitButtonTextKeys?.mainButton ?? 'ProcessPaymentPanelTitleForOptions')
        : lt(splitButtonTextKeys?.sideButton ?? 'ProcessPaymentDialogAddExternalPaymentsTitle');

    const [renderProcessPaymentDialog, toggleProcessPaymentDialog] = useProcessPaymentDialog({
        ...props,
        allowedPaymentTypes,
        title: dialogTitle,
        hideSinglePaymentMethodTitle:
            externalPaymentTypes.length === 1 && externalPaymentTypes.includes(ProcessPaymentType.ExternalCardPreauth),
        onClose: onFormClose,
    });

    const handleClick = (_hasChargeGuestPaymentTypes: boolean) => async () => {
        await onClick?.();
        setHasChargeGuestPaymentTypes(_hasChargeGuestPaymentTypes);
        toggleProcessPaymentDialog();
    };

    return (
        <>
            <SplitButton
                label={lt(splitButtonTextKeys?.mainButton ?? 'ProcessPaymentPanelTitleForOptions')}
                icon={buttonIcon ? IconName.Payments : undefined}
                variant="tertiary"
                onClick={handleClick(true)}
                loading={isLoading}
                wide
                data-test-id={DataTestId.ProcessPaymentButton}
            >
                <DropdownItem
                    label={lt(splitButtonTextKeys?.sideButton ?? 'ProcessPaymentDialogSplitButtonListItemPrimaryText')}
                    data-test-id={DataTestId.ProcessExternalPaymentButton}
                    onClick={handleClick(false)}
                />
                {additionalDropdownOptions}
            </SplitButton>

            {renderProcessPaymentDialog()}
        </>
    );
};

export const ProcessPaymentDialogButton = ({
    allowedPaymentTypes = DEFAULT_PROCESS_PAYMENT_TYPES,
    ...props
}: ProcessPaymentDialogButtonProps) => {
    const { chargeGuestPaymentTypes, externalPaymentTypes } = getFilteredProcessPaymentDialogTypes(allowedPaymentTypes);

    if (chargeGuestPaymentTypes.length === 0 || externalPaymentTypes.length === 0) {
        return <ProcessPaymentDialogSimpleButton allowedPaymentTypes={allowedPaymentTypes} {...props} />;
    }

    return (
        <ProcessPaymentDialogSplitButton
            chargeGuestPaymentTypes={chargeGuestPaymentTypes}
            externalPaymentTypes={externalPaymentTypes}
            {...props}
        />
    );
};
